import { useState, useEffect, useRef } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function PerformanceDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const dashboardRef = useRef<HTMLDivElement>(null);

  // Formatage en GNF
  const formatGNF = (value?: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }

  return value.toLocaleString('fr-GN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' GNF';
};


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [
          summaryRes,
          commercialRes,
          productsRes,
          monthlyRes,
          kpisRes,
        ] = await Promise.all([
          getResources('performance/summary'),
          getResources('performance/sales-by-commercial'),
          getResources('performance/top-products-margin'),
          getResources('performance/monthly-evolution'),
          getResources('performance/kpis'),
        ]);

        setData({
          summary: summaryRes.data,
          commercial: commercialRes.data,
          products: productsRes.data,
          monthly: monthlyRes.data,
          kpis: kpisRes.data,
        });
      } catch (error) {
        console.error('Erreur chargement données performance:', error);
        // Fallback avec tes données statiques
        setData({
          summary: {
            grossMargin: 186790,
            marginRate: 35.2,
            netProfit: 89450,
            profitRate: 16.8,
            roi: 24.5,
          },
          commercial: [
            { name: 'Jean Dupont', sales: 145600, orders: 45, margin: 51260 },
            { name: 'Sophie Durand', sales: 128900, orders: 38, margin: 45115 },
            { name: 'Pierre Martin', sales: 98750, orders: 32, margin: 34563 },
            { name: 'Marie Bernard', sales: 76340, orders: 28, margin: 26719 },
          ],
          products: [
            { name: 'Ordinateur Portable Dell XPS 15', sales: 67500, cost: 45000, margin: 22500, marginRate: 33.3 },
            { name: 'Écran Samsung 27" 4K', sales: 19000, cost: 11400, margin: 7600, marginRate: 40.0 },
            { name: 'Tablette Samsung Galaxy Tab S8', sales: 15600, cost: 10920, margin: 4680, marginRate: 30.0 },
            { name: 'Clavier Mécanique Logitech', sales: 7800, cost: 4680, margin: 3120, marginRate: 40.0 },
            { name: 'Souris Sans Fil Microsoft', sales: 3350, cost: 2010, margin: 1340, marginRate: 40.0 },
          ],
          monthly: [
            { month: 'Jan', revenue: 285600, cost: 185640, margin: 99960 },
            { month: 'Fév', revenue: 312400, cost: 203060, margin: 109340 },
            { month: 'Mar', revenue: 298750, cost: 194188, margin: 104562 },
            { month: 'Avr', revenue: 325800, cost: 211770, margin: 114030 },
            { month: 'Mai', revenue: 342580, cost: 222677, margin: 119903 },
            { month: 'Juin', revenue: 358900, cost: 233285, margin: 125615 },
          ],
          kpis: [
            { label: 'Taux de Conversion Devis', value: '68.5%', trend: '+5.2%', positive: true },
            { label: 'Délai Moyen de Livraison', value: '3.2 jours', trend: '-0.5j', positive: true },
            { label: 'Taux de Satisfaction Client', value: '94.8%', trend: '+2.1%', positive: true },
            { label: 'Rotation des Stocks', value: '8.5x/an', trend: '+1.2', positive: true },
            { label: 'Délai Moyen de Paiement', value: '42 jours', trend: '-3j', positive: true },
            { label: 'Taux de Retour Produits', value: '2.3%', trend: '-0.5%', positive: true },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const maxRevenue = data?.monthly?.length
  ? Math.max(...data.monthly.map((m: any) => m.revenue || 0))
  : 1;

    
  // Export Excel
  const exportToExcel = () => {
    if (!data) return;

    const rows = [
      ['RAPPORT DE PERFORMANCE COMMERCIALE'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [],
      ['INDICATEURS CLÉS'],
      ['Marge Brute', formatGNF(data.summary.grossMargin), `${data.summary.marginRate}%`],
      ['Bénéfice Net', formatGNF(data.summary.netProfit), `${data.summary.profitRate}%`],
      ['ROI', `${data.summary.roi}%`],
      [],
      ['KPI OPÉRATIONNELS'],
      ['Indicateur', 'Valeur', 'Évolution'],
      ...data.kpis.map((k: any) => [k.label, k.value, k.trend]),
      [],
      ['PERFORMANCE PAR COMMERCIAL'],
      ['Commercial', 'CA Réalisé', 'Nb Commandes', 'Marge Générée'],
      ...data.commercial.map((c: any) => [
        c.name,
        formatGNF(c.sales),
        c.orders,
        formatGNF(c.margin),
      ]),
      [],
      ['TOP 5 PRODUITS PAR MARGE'],
      ['Produit', 'Ventes', 'Coût', 'Marge', 'Taux Marge'],
      ...data.products.map((p: any) => [
        p.name,
        formatGNF(p.sales),
        formatGNF(p.cost),
        formatGNF(p.margin),
        `${p.marginRate}%`,
      ]),
      [],
      ['ÉVOLUTION MENSUELLE'],
      ['Mois', 'CA', 'Coûts', 'Marge', 'Taux Marge'],
      ...data.monthly.map((m: any) => [
        m.month,
        formatGNF(m.revenue),
        formatGNF(m.cost),
        formatGNF(m.margin),
        `${((m.margin / m.revenue) * 100).toFixed(1)}%`,
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Performance');

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `rapport_performance_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Export PDF
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    const canvas = await html2canvas(dashboardRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`rapport_performance_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-lg text-gray-500">Chargement du tableau de bord performance...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="text-red-600">Erreur de chargement des données.</div>;
  }

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* Header + Boutons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de Bord de Performance</h2>
          <p className="text-sm text-gray-600 mt-1">Analyse de la rentabilité et des performances commerciales</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-pdf-line"></i>
            Export PDF
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-excel-line"></i>
            Export Excel
          </button>
        </div>
      </div>

      {/* Cartes principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-line-chart-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium px-3 py-1 rounded bg-white/20">
              {data.summary.marginRate}%
            </span>
          </div>
          <p className="text-sm opacity-90 mb-1">Marge Brute</p>
          <p className="text-3xl font-bold">{formatGNF(data.summary.grossMargin)}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-funds-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium px-3 py-1 rounded bg-white/20">
              {data.summary.profitRate}%
            </span>
          </div>
          <p className="text-sm opacity-90 mb-1">Bénéfice Net</p>
          <p className="text-3xl font-bold">{formatGNF(data.summary.netProfit)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-percent-line text-2xl"></i>
            </div>
          </div>
          <p className="text-sm opacity-90 mb-1">Retour sur Investissement</p>
          <p className="text-3xl font-bold">{data.summary.roi}%</p>
        </div>
      </div>

      {/* KPI Opérationnels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.kpis.map((kpi: any, i: number) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{kpi.label}</span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                kpi.positive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {kpi.trend}
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
          </div>
        ))}
      </div>

      {/* Performance commerciale & Top produits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance par Commercial</h3>
          <div className="space-y-4">
            {data.commercial.map((c: any, i: number) => {
              const maxSales = data.commercial[0]?.sales || 1;
              return (
                <div key={i} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">
                          {c.name.split(' ').map((n: string) => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-500">{c.orders} commandes</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{formatGNF(c.sales)}</p>
                      <p className="text-xs text-green-600">Marge: {formatGNF(c.margin)}</p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                      style={{ width: `${(c.sales / maxSales) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Produits par Marge</h3>
          <div className="space-y-4">
            {data.products.map((p: any, i: number) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-900 flex-1">{p.name}</p>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full ml-2">
                    {p.marginRate}%
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Ventes</p>
                    <p className="font-semibold text-gray-900">{formatGNF(p.sales)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Coût</p>
                    <p className="font-semibold text-gray-900">{formatGNF(p.cost)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Marge</p>
                    <p className="font-semibold text-green-600">{formatGNF(p.margin)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Évolution mensuelle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Évolution Mensuelle de la Marge</h3>
        <div className="space-y-6">
          {data.monthly.map((m: any, i: number) => {
          const marginRate =
            m.revenue && m.margin
              ? ((m.margin / m.revenue) * 100).toFixed(1)
              : '0.0';
            return (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 w-16">{m.month}</span>
                  <div className="flex-1 flex items-center gap-6">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-blue-600">CA</span>
                        <span className="font-medium text-blue-700">{formatGNF(m.revenue)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                          style={{ width: `${(m.revenue / maxRevenue) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-red-600">Coûts</span>
                        <span className="font-medium text-red-700">{formatGNF(m.cost)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full"
                          style={{ width: `${(m.cost / maxRevenue) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-32 text-right">
                    <span className="text-sm font-bold text-green-600">{formatGNF(m.margin)}</span>
                    <p className="text-xs text-gray-500">{marginRate}%</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}